import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ComponentType, Ref } from 'react';
import { cn } from '@/lib/cn';

/** Any icon component (lucide icons and the direction-aware ones in icons.tsx). */
export type IconType = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: IconType;
  iconEnd?: IconType;
  ref?: Ref<HTMLButtonElement>;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-ink text-white hover:bg-ink-hover',
  secondary: 'border border-field-line/70 bg-surface text-ink hover:bg-hover',
  ghost: 'text-muted hover:bg-track hover:text-ink',
  danger: 'bg-danger text-white hover:bg-danger-text',
  accent: 'bg-accent text-white hover:bg-accent-hover',
  link: 'text-accent hover:text-accent-hover hover:underline underline-offset-4',
};

// 44px is the default touch target; "sm" is 36px with a mouse and grows to 44px on touch screens.
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm gap-1.5 pointer-coarse:h-11',
  md: 'h-11 px-4 text-base gap-2',
  lg: 'h-12 px-5 text-md font-semibold gap-2',
};

export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className?: string): string {
  return cn(
    'inline-flex shrink-0 items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors select-none',
    'disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
    variant === 'link' ? 'min-h-11 px-1 text-base pointer-fine:min-h-0' : SIZES[size],
    VARIANTS[variant],
    className,
  );
}

export function Button({ variant = 'secondary', size = 'md', loading, icon: Icon, iconEnd: IconEnd, className, children, disabled, type = 'button', ...rest }: ButtonProps) {
  const iconSize = size === 'sm' ? 'size-4' : 'size-[18px]';
  return (
    <button type={type} className={buttonClass(variant, size, className)} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <Loader2 aria-hidden className={cn(iconSize, 'animate-spin')} /> : Icon ? <Icon aria-hidden className={iconSize} /> : null}
      {children}
      {IconEnd && !loading ? <IconEnd aria-hidden className={iconSize} /> : null}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'icon' | 'iconEnd'> {
  icon: IconType;
  label: string;
}

export function IconButton({ icon: Icon, label, size = 'md', variant = 'ghost', className, ...rest }: IconButtonProps) {
  return (
    <Button variant={variant} size={size} aria-label={label} title={label} className={cn(size === 'sm' ? 'w-9 px-0 pointer-coarse:w-11' : 'w-11 px-0', className)} {...rest}>
      <Icon aria-hidden className={size === 'sm' ? 'size-4' : 'size-5'} />
    </Button>
  );
}
